import {CustomFieldType} from '@frontapp/workflows/custom_fields';
import {isString} from 'lodash';
import React, {FC} from 'react';
import styled from 'styled-components';

import {getValidTimestampFromValue} from '../../../../libs/shared/core/src/helpers/datetime/dateTimeHelpers';
import {Trans} from '../../../../libs/shared/core/src/helpers/internationalization/react';
import {SmartDateTimeFormatter} from '../../../../libs/shared/ui/src/components/datetime/smartDateTimeFormatter';
import {Icon} from '../../../../libs/shared/ui/src/components/icon/icon';
import {UserThemeConsumer} from '../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {buildCustomFieldLabel} from '../../../../srcCommon/helpers/models/customFieldHelpers';
import {CustomAttributeModel} from '../../../models/api/customFieldModel';
import {CustomAttributeInboxContainer} from '../../contacts/contactInformation/customAttributeInboxContainer';
import {CustomAttributeTeammateContainer} from '../../contacts/contactInformation/customAttributeTeammateContainer';
import {DomLinkifyRenderer} from '../formatters/markdown/domLinkifyRenderer';

interface CustomAttributeValueProps {
  customAttribute: CustomAttributeModel;
  editedValue?: string;
}

const StyledDatetimeValueDiv = styled.div`
  display: flex;
`;

const StyledCalendarIcon = styled(Icon)`
  margin-right: 8px;
`;

export const CustomAttributeValue: FC<CustomAttributeValueProps> = ({customAttribute, editedValue}) => {
  const {value, customField} = customAttribute;
  const renderedValue = isString(editedValue) ? editedValue : value;

  switch (customField.type) {
    case CustomFieldType.STRING:
      // User sometimes enter links - this lets those links be clickable.
      return <DomLinkifyRenderer text={renderedValue} />;

    case CustomFieldType.ENUM: {
      return <span>{buildCustomFieldLabel(renderedValue, customField) ?? '-'}</span>;
    }

    case CustomFieldType.DATETIME:
      return (
        <UserThemeConsumer>
          {(theme) => (
            <StyledDatetimeValueDiv>
              <StyledCalendarIcon name="calendarSmall" color={theme.greys.shade70} />
              <SmartDateTimeFormatter value={getValidTimestampFromValue(renderedValue)} />
            </StyledDatetimeValueDiv>
          )}
        </UserThemeConsumer>
      );

    case CustomFieldType.BOOLEAN:
      return renderedValue === 'true' ? (
        <Trans id="custom-attribute-value-yes">Yes</Trans>
      ) : (
        <Trans id="custom-attribute-value-no">No</Trans>
      );

    case CustomFieldType.TEAMMATE:
      return <CustomAttributeTeammateContainer teammateEmail={renderedValue} />;

    case CustomFieldType.INBOX:
      return <CustomAttributeInboxContainer inboxPublicId={renderedValue} />;

    default:
      return <span>{renderedValue}</span>;
  }
};
